import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Activity, Upload, LineChart, LogOut, User as UserIcon, ShieldAlert } from "lucide-react";
import { useAuth } from "../context/AuthContext"; 
import { Button } from "./ui/button"; 

const NavBar = () => {
  const loc = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const isActive = (p) => loc.pathname === p || (p !== "/" && loc.pathname.startsWith(p));
  
  const linkCls = (active) =>
    `px-3 py-2 text-sm font-bold rounded-md transition-all duration-200 flex items-center ${
      active 
        ? "bg-slate-900 text-white shadow-md" 
        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
    }`;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <nav className="border-b border-slate-200 bg-white/95 backdrop-blur-md sticky top-0 z-50 shadow-sm" data-testid="main-navbar">
      <div className="max-w-[1600px] mx-auto px-4 h-14 flex items-center justify-between">
        
        {/* LEFT: Logo & Main App Links */}
        <div className="flex items-center gap-6">
          <Link to="/" className="flex items-center gap-2 font-black text-slate-900" data-testid="navbar-logo">
            <Activity className="w-5 h-5 text-emerald-600" />
            <span style={{ fontFamily: "'JetBrains Mono', monospace" }} className="text-lg tracking-tight">StockSignal</span>
          </Link>
          
          {/* Main App links only visible to authenticated users */}
          {user && (
            <div className="hidden md:flex items-center gap-1.5 ml-2">
              <Link to="/rrg" className={linkCls(isActive("/rrg"))} data-testid="nav-rrg">
                RRG Matrix
              </Link>
              <Link to="/indicators" className={linkCls(isActive("/indicators"))} data-testid="nav-indicators">
                <LineChart className="w-4 h-4 mr-1.5" />Indicators
              </Link>
              
              {/* Strictly Admin Only Link */}
              {user.role === "admin" && (
                <Link to="/upload" className={linkCls(isActive("/upload"))} data-testid="nav-upload">
                  <ShieldAlert className="w-4 h-4 mr-1.5 text-rose-500" />Admin Console
                </Link>
              )}
            </div>
          )}
        </div>

        {/* RIGHT: Auth Controls & Profile */}
        <div className="flex items-center gap-3">
          {!user ? (
            <div className="flex items-center gap-3">
              <span className="hidden lg:inline-block text-xs font-bold text-slate-400 tracking-wide uppercase mr-4">
                Free NSE Bhavcopy Analytics
              </span>
              <Link to="/login">
                <Button variant="ghost" size="sm" className="font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-100">
                  Login
                </Button>
              </Link>
              <Link to="/register">
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-md">
                  Create Account
                </Button>
              </Link>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-full text-xs font-black text-slate-700 shadow-inner">
                <UserIcon className="w-3.5 h-3.5 text-sky-500" />
                {user.name}
                {user.role === 'admin' && (
                  <span className="bg-rose-100 text-rose-600 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest ml-1 shadow-sm">
                    Admin
                  </span>
                )}
              </div>
              
              <div className="h-5 w-px bg-slate-200 mx-1 hidden sm:block"></div>

              <Button onClick={handleLogout} variant="ghost" size="sm" className="text-slate-500 hover:text-rose-600 hover:bg-rose-50 font-bold transition-colors">
                <LogOut className="w-4 h-4 sm:mr-1.5" /> 
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          )}
        </div>

      </div>
    </nav>
  );
};

export default NavBar;